/**
 * Lookup and reference functions of the Core_Formula_Set (Requirement 4.1).
 *
 * Every "no match" path here ends in `#N/A` unless the call supplied a fallback,
 * which is Requirement 4.11; every out-of-range index ends in `#REF!`.
 *
 * `OFFSET` and `INDIRECT` return *references*, not values, so they compose with
 * the range-consuming functions (`SUM(OFFSET(A1,0,0,3,1))` works). `INDIRECT`
 * parses its argument with the same A1 parser the Formula_Parser uses and
 * resolves the sheet name through the context, so a name that does not exist
 * yields `#REF!` rather than a silent blank.
 */

import { parseA1Range } from '../../a1.js';
import { MAX_COLS, MAX_ROWS } from '../../types.js';
import {
  arrayOfGrid,
  compareScalars,
  err,
  gridOf,
  isError,
  makeRange,
  num,
  scalarsEqual,
  toScalar,
  type FnSpec,
  type Grid,
  type Scalar,
  type Value,
} from '../values.js';
import { boolArg, intArg, isFailure, optBoolArg, optIntArg, textArg, wildcardMatch } from './helpers.js';

/** A vector view of a grid, used by the lookup functions. */
interface Vector {
  length: number;
  get(i: number): Scalar;
  /** True when the underlying grid is laid out as a row. */
  horizontal: boolean;
}

/** Views a single-row or single-column grid as a vector. */
function vectorOf(grid: Grid): Vector | null {
  if (grid.rows === 1) {
    return { length: grid.cols, get: (i) => grid.get(0, i), horizontal: true };
  }
  if (grid.cols === 1) {
    return { length: grid.rows, get: (i) => grid.get(i, 0), horizontal: false };
  }
  return null;
}

/** Exact match, honouring wildcards when the key is a pattern. */
function exactMatch(key: Scalar, cell: Scalar): boolean {
  if (key.k === 'text' && /[*?]/.test(key.v)) {
    const t = cell.k === 'text' ? cell.v : null;
    return t !== null && wildcardMatch(t, key.v);
  }
  return scalarsEqual(key, cell);
}

/**
 * Approximate lookup over a vector assumed sorted ascending: the position of the
 * largest value not greater than `key`. Returns `-1` when every value is larger.
 */
function approximateAscending(key: Scalar, vector: Vector): number {
  let best = -1;
  for (let i = 0; i < vector.length; i++) {
    const cell = vector.get(i);
    if (cell.k === 'empty' || isError(cell)) continue;
    if (compareScalars(cell, key) <= 0) best = i;
    else break;
  }
  return best;
}

/**
 * Approximate lookup over a vector assumed sorted descending: the position of
 * the smallest value not less than `key`.
 */
function approximateDescending(key: Scalar, vector: Vector): number {
  let best = -1;
  for (let i = 0; i < vector.length; i++) {
    const cell = vector.get(i);
    if (cell.k === 'empty' || isError(cell)) continue;
    if (compareScalars(cell, key) >= 0) best = i;
    else break;
  }
  return best;
}

/** Nearest position under `XLOOKUP` match modes -1 and 1. */
function nearestMatch(key: Scalar, vector: Vector, prefer: 'smaller' | 'larger'): number {
  let best = -1;
  let bestCell: Scalar | null = null;
  for (let i = 0; i < vector.length; i++) {
    const cell = vector.get(i);
    if (cell.k === 'empty' || isError(cell)) continue;
    const cmp = compareScalars(cell, key);
    if (cmp === 0) return i;
    const wanted = prefer === 'smaller' ? cmp < 0 : cmp > 0;
    if (!wanted) continue;
    if (bestCell === null) {
      best = i;
      bestCell = cell;
      continue;
    }
    const better =
      prefer === 'smaller' ? compareScalars(cell, bestCell) > 0 : compareScalars(cell, bestCell) < 0;
    if (better) {
      best = i;
      bestCell = cell;
    }
  }
  return best;
}

/** Shared body of `VLOOKUP` and `HLOOKUP`. */
function tableLookup(orientation: 'v' | 'h'): FnSpec {
  return {
    name: orientation === 'v' ? 'VLOOKUP' : 'HLOOKUP',
    min: 3,
    max: 4,
    fn: (args, ctx) => {
      const key = toScalar(args[0] as Value, ctx);
      if (isError(key)) return key;
      const table = gridOf(args[1] as Value, ctx);
      const index = intArg(args, 2, ctx);
      if (isFailure(index)) return index.fail;
      const approx = optBoolArg(args, 3, ctx, true);
      if (isFailure(approx)) return approx.fail;

      const span = orientation === 'v' ? table.cols : table.rows;
      if (index < 1) return err('#VALUE!');
      if (index > span) return err('#REF!');

      const keys: Vector =
        orientation === 'v'
          ? { length: table.rows, get: (i) => table.get(i, 0), horizontal: false }
          : { length: table.cols, get: (i) => table.get(0, i), horizontal: true };

      let hit = -1;
      if (approx) {
        hit = approximateAscending(key, keys);
      } else {
        for (let i = 0; i < keys.length; i++) {
          if (exactMatch(key, keys.get(i))) {
            hit = i;
            break;
          }
        }
      }
      if (hit < 0) return err('#N/A');
      return orientation === 'v' ? table.get(hit, index - 1) : table.get(index - 1, hit);
    },
  };
}

export const LOOKUP_FUNCTIONS: readonly FnSpec[] = [
  tableLookup('v'),
  tableLookup('h'),
  {
    name: 'XLOOKUP',
    min: 3,
    max: 6,
    errorTolerant: true,
    fn: (args, ctx) => {
      const key = toScalar(args[0] as Value, ctx);
      if (isError(key)) return key;
      const lookupVector = vectorOf(gridOf(args[1] as Value, ctx));
      if (lookupVector === null) return err('#VALUE!');
      const returnGrid = gridOf(args[2] as Value, ctx);
      const matchMode = optIntArg(args, 4, ctx, 0);
      if (isFailure(matchMode)) return matchMode.fail;
      const searchMode = optIntArg(args, 5, ctx, 1);
      if (isFailure(searchMode)) return searchMode.fail;

      const order: number[] = [];
      for (let i = 0; i < lookupVector.length; i++) order.push(i);
      if (searchMode === -1) order.reverse();

      let hit = -1;
      if (matchMode === 0 || matchMode === 2) {
        for (const i of order) {
          const cell = lookupVector.get(i);
          const found =
            matchMode === 2 && key.k === 'text'
              ? cell.k === 'text' && wildcardMatch(cell.v, key.v)
              : exactMatch(key, cell);
          if (found) {
            hit = i;
            break;
          }
        }
      } else if (matchMode === -1 || matchMode === 1) {
        hit = nearestMatch(key, lookupVector, matchMode === -1 ? 'smaller' : 'larger');
      } else {
        return err('#VALUE!');
      }

      if (hit < 0) {
        const fallback = args[3];
        return fallback === undefined ? err('#N/A') : fallback;
      }
      // The return array is indexed along the same axis as the lookup vector.
      if (lookupVector.horizontal) {
        if (returnGrid.rows === 1) return returnGrid.get(0, hit);
        const column: Scalar[][] = [];
        for (let r = 0; r < returnGrid.rows; r++) column.push([returnGrid.get(r, hit)]);
        return { k: 'array', rows: returnGrid.rows, cols: 1, cells: column };
      }
      if (returnGrid.cols === 1) return returnGrid.get(hit, 0);
      const row: Scalar[] = [];
      for (let c = 0; c < returnGrid.cols; c++) row.push(returnGrid.get(hit, c));
      return { k: 'array', rows: 1, cols: row.length, cells: [row] };
    },
  },
  {
    name: 'INDEX',
    min: 2,
    max: 3,
    fn: (args, ctx) => {
      const grid = gridOf(args[0] as Value, ctx);
      const rowArg = intArg(args, 1, ctx);
      if (isFailure(rowArg)) return rowArg.fail;
      const colArg = optIntArg(args, 2, ctx, 0);
      if (isFailure(colArg)) return colArg.fail;
      if (rowArg < 0 || colArg < 0) return err('#VALUE!');

      // A single index into a vector addresses along the vector's own axis.
      if (args[2] === undefined && (grid.rows === 1 || grid.cols === 1) && rowArg > 0) {
        const vector = vectorOf(grid) as Vector;
        if (rowArg > vector.length) return err('#REF!');
        return vector.get(rowArg - 1);
      }
      if (rowArg > grid.rows || colArg > grid.cols) return err('#REF!');
      if (rowArg === 0 && colArg === 0) return arrayOfGrid(grid);
      if (rowArg === 0) {
        const cells: Scalar[][] = [];
        for (let r = 0; r < grid.rows; r++) cells.push([grid.get(r, colArg - 1)]);
        return { k: 'array', rows: grid.rows, cols: 1, cells };
      }
      if (colArg === 0) {
        const row: Scalar[] = [];
        for (let c = 0; c < grid.cols; c++) row.push(grid.get(rowArg - 1, c));
        return { k: 'array', rows: 1, cols: row.length, cells: [row] };
      }
      return grid.get(rowArg - 1, colArg - 1);
    },
  },
  {
    name: 'MATCH',
    min: 2,
    max: 3,
    fn: (args, ctx) => {
      const key = toScalar(args[0] as Value, ctx);
      if (isError(key)) return key;
      const vector = vectorOf(gridOf(args[1] as Value, ctx));
      if (vector === null) return err('#N/A');
      const mode = optIntArg(args, 2, ctx, 1);
      if (isFailure(mode)) return mode.fail;

      let hit = -1;
      if (mode === 0) {
        for (let i = 0; i < vector.length; i++) {
          if (exactMatch(key, vector.get(i))) {
            hit = i;
            break;
          }
        }
      } else if (mode > 0) {
        hit = approximateAscending(key, vector);
      } else {
        hit = approximateDescending(key, vector);
      }
      return hit < 0 ? err('#N/A') : num(hit + 1);
    },
  },
  {
    name: 'OFFSET',
    min: 3,
    max: 5,
    fn: (args, ctx) => {
      const base = args[0];
      if (base === undefined || base.k !== 'ref') return err('#VALUE!');
      const dRow = intArg(args, 1, ctx);
      if (isFailure(dRow)) return dRow.fail;
      const dCol = intArg(args, 2, ctx);
      if (isFailure(dCol)) return dCol.fail;
      const range = base.range;
      const height = optIntArg(args, 3, ctx, range.r1 - range.r0 + 1);
      if (isFailure(height)) return height.fail;
      const width = optIntArg(args, 4, ctx, range.c1 - range.c0 + 1);
      if (isFailure(width)) return width.fail;
      if (height <= 0 || width <= 0) return err('#REF!');
      const r0 = range.r0 + dRow;
      const c0 = range.c0 + dCol;
      const r1 = r0 + height - 1;
      const c1 = c0 + width - 1;
      if (r0 < 0 || c0 < 0 || r1 >= MAX_ROWS || c1 >= MAX_COLS) return err('#REF!');
      return { k: 'ref', range: makeRange(range.sheet, r0, c0, r1, c1) };
    },
  },
  {
    name: 'INDIRECT',
    min: 1,
    max: 2,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      if (isFailure(source)) return source.fail;
      const a1Style = args[1] === undefined ? true : boolArg(args, 1, ctx);
      if (isFailure(a1Style)) return a1Style.fail;
      // R1C1 addressing is outside the supported grammar.
      if (a1Style === false) return err('#REF!');
      const parsed = parseA1Range(source.trim());
      if (parsed === null) return err('#REF!');
      const sheetName = parsed.sheet;
      const sheet = sheetName === undefined ? ctx.at.sheet : ctx.resolveSheet(sheetName);
      if (sheet === null) return err('#REF!');
      return {
        k: 'ref',
        range: makeRange(sheet, parsed.from.row, parsed.from.col, parsed.to.row, parsed.to.col),
      };
    },
  },
];
